/**
 * UK working time for approval reminders: Monday–Friday, skipping England &
 * Wales bank holidays, in Europe/London. Bank holidays are computed from the
 * statutory rules (no live feed); one-off changes by proclamation go in
 * BANK_HOLIDAY_OVERRIDES.
 */

import type { ApprovalStage } from './submissions.js';

export const APPROVAL_STAGE_LABEL: Record<ApprovalStage, string> = {
  cs: 'Customer Service',
  accounts: 'Accounts',
  supplier: 'Supplier',
};

/** Settings → Approval reminders (setting key `approval_reminders`). */
export interface ReminderSettings {
  enabled: boolean;
  /** Working days an application may wait at each stage before one reminder. */
  days: Record<ApprovalStage, number>;
}

export const MAX_REMINDER_DAYS = 20;
export const DEFAULT_REMINDER_SETTINGS: ReminderSettings = {
  enabled: true,
  days: { cs: 2, accounts: 2, supplier: 2 },
};

/** Fill gaps with defaults and clamp each stage to 1–20 whole working days. */
export function normaliseReminderSettings(raw: unknown): ReminderSettings {
  const o = (raw && typeof raw === 'object' ? raw : {}) as Partial<ReminderSettings>;
  const days = (o.days && typeof o.days === 'object' ? o.days : {}) as Partial<Record<ApprovalStage, unknown>>;
  const clamp = (v: unknown, d: number) => {
    const n = typeof v === 'number' && Number.isFinite(v) ? Math.round(v) : d;
    return Math.min(MAX_REMINDER_DAYS, Math.max(1, n));
  };
  return {
    enabled: typeof o.enabled === 'boolean' ? o.enabled : DEFAULT_REMINDER_SETTINGS.enabled,
    days: {
      cs: clamp(days.cs, DEFAULT_REMINDER_SETTINGS.days.cs),
      accounts: clamp(days.accounts, DEFAULT_REMINDER_SETTINGS.days.accounts),
      supplier: clamp(days.supplier, DEFAULT_REMINDER_SETTINGS.days.supplier),
    },
  };
}

// ---------------------------------------------------------------------------
// Calendar dates (y, m 1–12, d) — plain UTC arithmetic, no time zone.

interface Ymd {
  y: number;
  m: number;
  d: number;
}
const key = ({ y, m, d }: Ymd) => `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
const fromUtc = (t: number): Ymd => {
  const x = new Date(t);
  return { y: x.getUTCFullYear(), m: x.getUTCMonth() + 1, d: x.getUTCDate() };
};
const addDays = (a: Ymd, n: number): Ymd => fromUtc(Date.UTC(a.y, a.m - 1, a.d + n));
/** 0 = Sunday … 6 = Saturday. */
const weekday = (a: Ymd) => new Date(Date.UTC(a.y, a.m - 1, a.d)).getUTCDay();
const isWeekend = (a: Ymd) => weekday(a) === 0 || weekday(a) === 6;

/** Easter Sunday (Anonymous Gregorian algorithm). */
function easter(y: number): Ymd {
  const a = y % 19;
  const b = Math.floor(y / 100);
  const c = y % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return { y, m: month, d: day };
}

function firstMonday(y: number, m: number): Ymd {
  const first = { y, m, d: 1 };
  return addDays(first, (8 - weekday(first)) % 7);
}
function lastMonday(y: number, m: number): Ymd {
  const last = fromUtc(Date.UTC(y, m, 0));
  return addDays(last, -((weekday(last) + 6) % 7));
}

/**
 * One-off changes by royal proclamation: `add` extra days, `remove` a rule date
 * that was moved. Example (past): 2022 moved the spring holiday to 02/06 and
 * added 03/06 and 19/09. Add future ones here when announced.
 */
export const BANK_HOLIDAY_OVERRIDES: { add: string[]; remove: string[] } = {
  add: ['2020-05-08', '2022-06-02', '2022-06-03', '2022-09-19', '2023-05-08'],
  remove: ['2020-05-04', '2022-05-30'],
};

const cache = new Map<number, Set<string>>();

/** England & Wales bank holidays for a year, as YYYY-MM-DD. */
export function bankHolidays(y: number): Set<string> {
  const hit = cache.get(y);
  if (hit) return hit;
  const days: string[] = [];
  // New Year's Day: the next weekday if it falls at a weekend.
  let ny: Ymd = { y, m: 1, d: 1 };
  while (isWeekend(ny)) ny = addDays(ny, 1);
  days.push(key(ny));
  const e = easter(y);
  days.push(key(addDays(e, -2)), key(addDays(e, 1)));
  days.push(key(firstMonday(y, 5)), key(lastMonday(y, 5)), key(lastMonday(y, 8)));
  // Christmas and Boxing Day: each one at a weekend moves to the next free weekday.
  const taken = new Set<string>();
  for (const d of [25, 26]) {
    let x: Ymd = { y, m: 12, d };
    while (isWeekend(x) || taken.has(key(x))) x = addDays(x, 1);
    taken.add(key(x));
    days.push(key(x));
  }
  const set = new Set(days.filter((d) => !BANK_HOLIDAY_OVERRIDES.remove.includes(d)));
  for (const d of BANK_HOLIDAY_OVERRIDES.add) if (d.startsWith(`${y}-`)) set.add(d);
  cache.set(y, set);
  return set;
}

const isWorkingDate = (a: Ymd) => !isWeekend(a) && !bankHolidays(a.y).has(key(a));

// ---------------------------------------------------------------------------
// Europe/London wall-clock <-> instants.

const londonFmt = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/London',
  hourCycle: 'h23',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});

interface Wall extends Ymd {
  h: number;
  mi: number;
  s: number;
}
function london(t: number): Wall {
  const p = Object.fromEntries(londonFmt.formatToParts(new Date(t)).map((x) => [x.type, x.value]));
  return { y: +p.year!, m: +p.month!, d: +p.day!, h: +p.hour!, mi: +p.minute!, s: +p.second! };
}
/** The instant a London wall-clock time happens (DST gaps resolve forward). */
function fromLondon(w: Wall): number {
  const asUtc = Date.UTC(w.y, w.m - 1, w.d, w.h, w.mi, w.s);
  const offsetAt = (t: number) => {
    const l = london(t);
    return Date.UTC(l.y, l.m - 1, l.d, l.h, l.mi, l.s) - t;
  };
  const first = asUtc - offsetAt(asUtc);
  return asUtc - offsetAt(first);
}

/** Is this instant on a UK working day (London date)? */
export function isWorkingDay(at: Date | string): boolean {
  return isWorkingDate(london(new Date(at).getTime()));
}

/** Working day, 08:00–18:00 London — when reminders may go out. */
export function isWorkingHours(at: Date | string): boolean {
  const w = london(new Date(at).getTime());
  return isWorkingDate(w) && w.h >= 8 && w.h < 18;
}

/**
 * `days` working days after `from`, at the same London time of day. Time that
 * starts outside a working day counts from 00:00 on the next one, so Saturday
 * + 2 working days is the end of Tuesday.
 */
export function addWorkingDays(from: Date | string, days: number): Date {
  const w = london(new Date(from).getTime());
  let date: Ymd = { y: w.y, m: w.m, d: w.d };
  let time = { h: w.h, mi: w.mi, s: w.s };
  if (!isWorkingDate(date)) {
    while (!isWorkingDate(date)) date = addDays(date, 1);
    time = { h: 0, mi: 0, s: 0 };
  }
  for (let i = 0; i < days; i++) {
    do date = addDays(date, 1);
    while (!isWorkingDate(date));
  }
  return new Date(fromLondon({ ...date, ...time }));
}

/** How long an application has waited at its current approval stage. */
export interface ApprovalTiming {
  /** When it reached this stage (submitted, or the previous stage's decision). */
  waitingSince: string;
  /** The stage's working-day limit. */
  days: number;
  dueAt: string;
  /** Past its limit (always false while reminders are switched off). */
  overdue: boolean;
  /** When this stage's one reminder went out, if it has. */
  reminderSentAt: string | null;
}
